import { create } from 'zustand';
import { getDb } from '../db/handle';
import { readMeta, setMeta } from '../db/idb';
import { getServerUrl, isNative } from '../native/platform';
import { tokenStore } from '../native/tokenStore';
import { ApiError, NetworkError, api, type BoardInfo, type Credentials, type LabelDefaults, type MetaResponse, type PublicUser, type SessionResponse } from '../sync/api';
import { startSync, stopSync } from '../sync/engine';
import { useCards } from './cards';
import { orderBoards } from '../lib/boards';
import { useEvents } from './events';
import { useSettings } from './settings';

export type SessionStatus = 'loading' | 'anonymous' | 'authed';

interface CachedSession {
  user: PublicUser;
  boards: BoardInfo[];
  defaults: LabelDefaults;
  deviceId: string;
}

export interface SessionState {
  status: SessionStatus;
  token: string | null;
  user: PublicUser | null;
  boards: BoardInfo[];
  boardId: string | null;
  defaults: LabelDefaults;
  meta: MetaResponse | null;
  /** The server could not be reached during init; the cached account is in use. */
  offline: boolean;
  /** The account was created in this session: the board shows the tour once. */
  freshAccount: boolean;
  init(): Promise<void>;
  refreshMeta(): Promise<MetaResponse | null>;
  setup(c: Credentials): Promise<void>;
  login(c: Credentials): Promise<void>;
  register(c: Credentials, inviteToken?: string): Promise<void>;
  logout(): Promise<void>;
  changePassword(next: string, current?: string): Promise<void>;
  setEmail(email: string | null): Promise<void>;
  refreshMe(): Promise<void>;
  /** The realm on screen; remembered on this device. */
  setBoard(id: string): void;
  createBoard(name: string): Promise<void>;
  /** The next board in the user's order (1) or the previous (-1), round and round; false with a single board. */
  stepBoard(dir: 1 | -1): boolean;
  renameBoard(id: string, name: string): Promise<void>;
  deleteBoard(id: string): Promise<void>;
  /** A request came back 401: the token is dead. */
  handleUnauthorized(): Promise<void>;
  markPasswordRequired(): void;
}

/** Runtime pieces that must act before a sign-out, such as dropping a browser push subscription. */
export const sessionHooks: { beforeSignOut: (() => Promise<void>)[] } = { beforeSignOut: [] };

async function runSignOutHooks(): Promise<void> {
  for (const hook of sessionHooks.beforeSignOut) {
    try {
      await hook();
    } catch {
      // a hook must never keep the user signed in
    }
  }
}

const NO_DEFAULTS: LabelDefaults = { columnLabels: [null, null, null], bandLabels: [null, null, null], locale: 'auto', timeFormat: 'auto' };

const BOARD_KEY = 'hmawiw-board';

/** The board this device was on last if it is still one of the user's, else the first. */
function pickBoard(boards: BoardInfo[]): string | null {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(BOARD_KEY);
  } catch {
    // no storage
  }
  return boards.find((b) => b.id === stored)?.id ?? orderBoards(boards, useSettings.getState().boardOrder)[0]?.id ?? null;
}

function rememberBoard(id: string | null): void {
  try {
    if (id) localStorage.setItem(BOARD_KEY, id);
  } catch {
    // no storage
  }
}

export const useSession = create<SessionState>((set, get) => {
  async function becomeAuthed(token: string, data: CachedSession, meta: MetaResponse | null): Promise<void> {
    const db = getDb();
    const previousUser = await readMeta<string>(db, 'userId');
    if (previousUser && previousUser !== data.user.id) {
      // Another account on the same device: its data never mixes with this one.
      stopSync();
      await useCards.getState().reset();
      useSettings.getState().reset();
      useEvents.getState().reset();
    }
    await setMeta(db, 'userId', data.user.id);
    await setMeta(db, 'session', data);
    const home = data.boards[0]?.id ?? null;
    if (home) await useCards.getState().adoptBoard(home);
    const boardId = pickBoard(data.boards);
    useCards.getState().setCurrentBoard(boardId);
    if (meta) {
      const stored = await readMeta<string>(db, 'installId');
      if (!stored) await setMeta(db, 'installId', meta.installId);
    }
    set({ status: 'authed', token, user: data.user, boards: data.boards, boardId, defaults: data.defaults, offline: false });
    startSync();
  }

  /** The boards changed on the server: the list on screen and the cached session follow. */
  async function adoptBoards(boards: BoardInfo[]): Promise<void> {
    const cached = await readMeta<CachedSession>(getDb(), 'session');
    if (cached) await setMeta(getDb(), 'session', { ...cached, boards });
    set({ boards });
  }

  async function adopt(res: SessionResponse, meta: MetaResponse | null): Promise<void> {
    await tokenStore().set(res.token);
    await becomeAuthed(res.token, { user: res.user, boards: res.boards, defaults: res.defaults, deviceId: res.deviceId }, meta);
  }

  return {
    status: 'loading',
    token: null,
    user: null,
    boards: [],
    boardId: null,
    defaults: NO_DEFAULTS,
    meta: null,
    offline: false,
    freshAccount: false,

    async init() {
      if (isNative && !getServerUrl()) {
        // Nothing to talk to yet: the sign-in route sends the phone to the server-address form.
        set({ status: 'anonymous', token: null });
        return;
      }
      const token = await tokenStore().get();
      const meta = await get().refreshMeta();
      if (!token) {
        set({ status: 'anonymous', token: null });
        return;
      }
      try {
        const me = await api.me(token);
        await becomeAuthed(token, me, meta);
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          if (e.code === 'PASSWORD_CHANGE_REQUIRED') {
            const cached = await readMeta<CachedSession>(getDb(), 'session');
            if (cached) {
              set({ status: 'authed', token, user: { ...cached.user, mustChangePassword: true }, boards: cached.boards, boardId: pickBoard(cached.boards), defaults: cached.defaults });
              return;
            }
          }
          await tokenStore().clear();
          set({ status: 'anonymous', token: null, user: null });
          return;
        }
        const cached = await readMeta<CachedSession>(getDb(), 'session');
        if (cached) {
          const boardId = pickBoard(cached.boards);
          useCards.getState().setCurrentBoard(boardId);
          set({ status: 'authed', token, user: cached.user, boards: cached.boards, boardId, defaults: cached.defaults, offline: true });
          startSync();
          return;
        }
        set({ status: 'anonymous', token: null, offline: true });
      }
    },

    async refreshMeta() {
      try {
        const meta = await api.meta();
        set({ meta });
        return meta;
      } catch (e) {
        // Unreachable or not a hmawiw server: keep what we knew; the screens say so.
        if (e instanceof NetworkError || e instanceof ApiError) return get().meta;
        throw e;
      }
    },

    async setup(c) {
      await adopt(await api.setup(c), get().meta);
      set({ freshAccount: true });
      await get().refreshMeta();
    },

    async login(c) {
      await adopt(await api.login(c), get().meta);
    },

    async register(c, inviteToken) {
      await adopt(await api.register({ ...c, ...(inviteToken ? { inviteToken } : {}) }), get().meta);
      set({ freshAccount: true });
    },

    async setEmail(email) {
      const { token } = get();
      if (!token) return;
      const res = await api.setEmail(token, email);
      const cached = await readMeta<CachedSession>(getDb(), 'session');
      if (cached) await setMeta(getDb(), 'session', { ...cached, user: res.user });
      set({ user: res.user });
    },

    async logout() {
      const { token } = get();
      stopSync();
      await runSignOutHooks();
      if (token) {
        try {
          await api.logout(token);
        } catch {
          // the token dies with the local copy either way
        }
      }
      await tokenStore().clear();
      await useCards.getState().reset();
      useSettings.getState().reset();
      useEvents.getState().reset();
      set({ status: 'anonymous', token: null, user: null, boards: [], boardId: null, defaults: NO_DEFAULTS });
    },

    async changePassword(next, current) {
      const { token, user } = get();
      if (!token) throw new ApiError(401, 'UNAUTHORIZED', 'Sign in required');
      await api.changePassword(token, next, current);
      if (user) {
        const updated = { ...user, mustChangePassword: false };
        set({ user: updated });
        const cached = await readMeta<CachedSession>(getDb(), 'session');
        if (cached) await setMeta(getDb(), 'session', { ...cached, user: updated });
      }
      startSync();
    },

    async refreshMe() {
      const { token } = get();
      if (!token) return;
      const me = await api.me(token);
      await setMeta(getDb(), 'session', me);
      const boardId = pickBoard(me.boards);
      useCards.getState().setCurrentBoard(boardId);
      set({ user: me.user, boards: me.boards, boardId, defaults: me.defaults, offline: false });
    },

    setBoard(id) {
      if (!get().boards.some((b) => b.id === id)) return;
      rememberBoard(id);
      useCards.getState().setCurrentBoard(id);
      set({ boardId: id });
    },

    stepBoard(dir) {
      const ordered = orderBoards(get().boards, useSettings.getState().boardOrder);
      if (ordered.length < 2) return false;
      const at = Math.max(0, ordered.findIndex((b) => b.id === get().boardId));
      const next = ordered[(at + dir + ordered.length) % ordered.length];
      if (!next) return false;
      get().setBoard(next.id);
      return true;
    },

    async createBoard(name) {
      const { token } = get();
      if (!token) return;
      const res = await api.createBoard(token, name);
      await adoptBoards(res.boards);
      get().setBoard(res.board.id);
    },

    async renameBoard(id, name) {
      const { token } = get();
      if (!token) return;
      await adoptBoards((await api.renameBoard(token, id, name)).boards);
    },

    async deleteBoard(id) {
      const { token } = get();
      if (!token) return;
      const res = await api.deleteBoard(token, id);
      await adoptBoards(res.boards);
      if (get().boardId === id) get().setBoard(orderBoards(res.boards, useSettings.getState().boardOrder)[0]?.id ?? '');
    },

    async handleUnauthorized() {
      stopSync();
      await runSignOutHooks();
      await tokenStore().clear();
      set({ status: 'anonymous', token: null, user: null });
    },

    markPasswordRequired() {
      const { user } = get();
      if (user && !user.mustChangePassword) set({ user: { ...user, mustChangePassword: true } });
    },
  };
});
